import React, {useEffect, useMemo, useState} from 'react';
import Menu from '../Menu';
import PageHeader from '../PageHeader';
import GlobalVariable from '../../GlobalVariable';
import api, {isUnauthorizedError, redirectToLogin} from '../../api';
import './PersonalSchedule.scss';

type Shift = {companyId: number; address: string; from: string; to: string; onlineRecord: boolean};
type ScheduleDay = {date: string; shifts: Shift[]};
type PersonalScheduleData = {
    month: string;
    hasStaffRecord: boolean;
    canViewTeam: boolean;
    canEditSchedule?: boolean;
    workDays: number;
    salons: {id: number; address: string}[];
    days: ScheduleDay[];
};

const dateParts = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
const currentMonth = () => dateParts(new Date()).slice(0, 7);
const changeMonth = (month: string, step: number) => {
    const [year, number] = month.split('-').map(Number);
    const next = new Date(year, number - 1 + step, 1);
    return dateParts(next).slice(0, 7);
};
const localDate = (date: string) => new Date(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)), 12);
const monthName = (month: string) => new Intl.DateTimeFormat('ru-RU', {month: 'long', year: 'numeric'}).format(localDate(month + '-01'));
const dayName = (date: string) => new Intl.DateTimeFormat('ru-RU', {weekday: 'long'}).format(localDate(date));
const longDay = (date: string) => new Intl.DateTimeFormat('ru-RU', {day: 'numeric', month: 'long'}).format(localDate(date));
const shiftCount = (count: number) => {
    const lastTwo = count % 100;
    const last = count % 10;
    return `${count} ${lastTwo >= 11 && lastTwo <= 14 ? 'дней' : last === 1 ? 'день' : last >= 2 && last <= 4 ? 'дня' : 'дней'}`;
};

export default function PersonalSchedule({onShowTeam}: {onShowTeam: () => void}) {
    const [month, setMonth] = useState(currentMonth);
    const [data, setData] = useState<PersonalScheduleData | null>(null);
    const [selectedDate, setSelectedDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const today = dateParts(new Date());

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        setData(null);
        api({url: GlobalVariable.host() + '/widget/staff/my-date-schedule/' + month, method: 'GET', withCredentials: true})
            .then(response => {
                if (!active) return;
                const next = response.data as PersonalScheduleData;
                if (!next || !Array.isArray(next.days) || !Array.isArray(next.salons)) throw new Error('invalid_schedule');
                setData(next);
                const upcoming = next.days.find(day => day.date >= today && day.shifts.length > 0);
                const firstShift = next.days.find(day => day.shifts.length > 0);
                setSelectedDate((upcoming || firstShift || next.days.find(day => day.date === today) || next.days[0])?.date || '');
                setLoading(false);
            }).catch(reason => {
                if (!active) return;
                if (isUnauthorizedError(reason)) {
                    redirectToLogin();
                    return;
                }
                setError('Не удалось загрузить смены. Проверьте соединение и попробуйте ещё раз.');
                setLoading(false);
            });
        return () => { active = false; };
    }, [month, reload, today]);

    const selectedDay = data?.days.find(day => day.date === selectedDate);
    const workDays = data?.days.filter(day => day.shifts.length > 0) || [];
    const firstWeekday = data?.days.length ? (localDate(data.days[0].date).getDay() + 6) % 7 : 0;
    const salonTone = useMemo(() => {
        const tones: Record<number, number> = {};
        (data?.salons || []).forEach((salon, index) => {tones[salon.id] = index % 5;});
        return tones;
    }, [data]);

    return <div className='main personal-schedule-page'>
        <div className='content mx-w-content'>
            <PageHeader pageTitle='График работы' backBtnUrl='/'/>
            <div className='pt-header'/>
            <div className='personal-schedule-body'>
                <div className='personal-schedule-heading'>
                    <div><h2>Мои смены</h2><p>В каком салоне вы работаете в каждый день</p></div>
                    {(data?.canViewTeam || data?.canEditSchedule) && <button type='button' className='personal-schedule-team-link' onClick={onShowTeam}>{data.canViewTeam ? 'График команды' : 'Изменить смены'} <span aria-hidden='true'>→</span></button>}
                </div>

                <div className='personal-schedule-month' aria-label='Выбранный месяц'>
                    <button type='button' aria-label='Предыдущий месяц' onClick={() => setMonth(value => changeMonth(value, -1))}>‹</button>
                    <strong>{monthName(month)}</strong>
                    <button type='button' aria-label='Следующий месяц' onClick={() => setMonth(value => changeMonth(value, 1))}>›</button>
                </div>

                {loading && <div className='personal-schedule-state' role='status'>Загружаем ваши смены…</div>}
                {error && <div className='personal-schedule-state is-error' role='alert'>{error}<button type='button' onClick={() => setReload(value => value + 1)}>Повторить</button></div>}

                {!loading && !error && data && <>
                    {data.hasStaffRecord ? <>
                        {data.workDays > 0 && <div className='personal-schedule-summary'><strong>{shiftCount(data.workDays)} со сменами</strong><span>в {data.salons.length} {data.salons.length === 1 ? 'салоне' : 'салонах'} за месяц</span></div>}
                        <section className='personal-schedule-calendar' aria-label={`Календарь смен на ${monthName(month)}`}>
                            <div className='personal-schedule-weekdays' aria-hidden='true'>{['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(label => <span key={label}>{label}</span>)}</div>
                            <div className='personal-schedule-grid'>
                                {Array.from({length: firstWeekday}, (_, index) => <span className='personal-schedule-blank' key={`blank-${index}`}/>)}
                                {data.days.map(day => <button key={day.date} type='button' aria-pressed={selectedDate === day.date}
                                    aria-label={`${longDay(day.date)}: ${day.shifts.length ? day.shifts.map(shift => `${shift.address}, ${shift.from}–${shift.to}`).join('; ') : 'смен нет'}`}
                                    className={`${day.shifts.length ? 'has-shift' : ''} ${selectedDate === day.date ? 'is-selected' : ''} ${today === day.date ? 'is-today' : ''}`}
                                    onClick={() => setSelectedDate(day.date)}>
                                    <span>{Number(day.date.slice(-2))}</span>
                                    <span className='personal-schedule-dots' aria-hidden='true'>
                                        {Array.from(new Set(day.shifts.map(shift => shift.companyId))).slice(0, 3).map(id => <i className={`tone-${salonTone[id] ?? 0}`} key={id}/>)}
                                    </span>
                                </button>)}
                            </div>
                        </section>

                        {data.salons.length > 1 && <div className='personal-schedule-legend' aria-label='Цвета салонов'>
                            {data.salons.map(salon => <span key={salon.id}><i className={`tone-${salonTone[salon.id]}`}/>{salon.address}</span>)}
                        </div>}

                        {selectedDay && <section className='personal-schedule-selected' aria-live='polite'>
                            <div className='personal-schedule-selected-head'><span>{dayName(selectedDay.date)}</span><strong>{longDay(selectedDay.date)}</strong></div>
                            {selectedDay.shifts.length ? selectedDay.shifts.map((shift, index) => <div className='personal-schedule-shift' key={`${shift.companyId}-${shift.from}-${index}`}>
                                <i className={`tone-${salonTone[shift.companyId] ?? 0}`} aria-hidden='true'/>
                                <div><strong>{shift.address}</strong><span>{shift.from}–{shift.to}</span></div>
                            </div>) : <p className='personal-schedule-day-off'>На этот день смен нет.</p>}
                        </section>}

                        {workDays.length ? <section className='personal-schedule-list'>
                            <h3>Все рабочие дни</h3>
                            {workDays.map(day => <button key={day.date} type='button' className={selectedDate === day.date ? 'is-selected' : ''} onClick={() => setSelectedDate(day.date)}>
                                <span className='personal-schedule-list-date'><strong>{Number(day.date.slice(-2))}</strong><small>{dayName(day.date).slice(0, 2)}</small></span>
                                <span className='personal-schedule-list-shifts'>{day.shifts.map((shift, index) => <span key={`${shift.companyId}-${index}`}><i className={`tone-${salonTone[shift.companyId] ?? 0}`}/><strong>{shift.address}</strong><small>{shift.from}–{shift.to}</small></span>)}</span>
                                <span className='personal-schedule-list-arrow' aria-hidden='true'>›</span>
                            </button>)}
                        </section> : <div className='personal-schedule-empty'>На этот месяц смен пока нет. Если график уже согласован, уточните его у руководителя.</div>}
                    </> : <div className='personal-schedule-empty'>Профиль мастера пока не связан с салоном. Попросите руководителя проверить доступ.</div>}
                </>}
            </div>
        </div>
        <Menu/>
    </div>;
}
